if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}
/**
 *  @class
 *  @function ThemeHeader
 */

if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = this.querySelector('.mobile-toggle-wrapper');

      this.isTransitioning = false;
      this.lastScrollPos = 0;

      document.addEventListener('keyup', (e) => {
        if (e.code) {
          if (e.code.toUpperCase() === 'ESCAPE') {
            this.toggle.removeAttribute('open');
            this.toggle.classList.remove('active');
          }
        }
      });
      if (this.classList.contains('header-sticky--active')) {
        document.body.classList.add('header-sticky--active');
      }
      this.toggle.querySelector('.mobile-toggle').addEventListener('click', (e) => {
        if (this.toggle.classList.contains('active')) {
          e.preventDefault();
          document.body.classList.remove('overflow-hidden');
          this.toggle.classList.remove('active');
          this.closeAnimation(this.toggle);
        } else {
          document.body.classList.add('overflow-hidden');
          setTimeout(() => {
            this.toggle.classList.add('active');
          });
        }
      });

      // Coalesce all scroll-driven header work into a single rAF-throttled
      // handler so layout reads and CSS-var writes are batched together in one
      // frame (avoids synchronous layout thrashing / forced reflows).
      this._varCache = {};
      this._announcementBar = document.querySelector('.announcement-bar-section');
      this._scrollTicking = false;

      window.addEventListener(
        'scroll',
        () => {
          if (this._scrollTicking) return;
          this._scrollTicking = true;
          window.requestAnimationFrame(() => {
            this._scrollTicking = false;
            this.onScrollFrame();
          });
        },
        { passive: true }
      );

      // Initial measurement.
      this.onScrollFrame();
      this.measureHeader();

      // Re-measure header height after fonts and all resources are loaded.
      // On hard reload fonts are not cached, so the initial measurement above
      // captures a wrong height based on fallback fonts.
      document.fonts.ready.then(() => this.measureHeader());
      window.addEventListener('load', () => this.measureHeader());
      window.addEventListener('resize', debounce(() => this.measureHeader(), 100), {
        passive: true,
      });

      // ResizeObserver ensures --header-height updates immediately whenever
      // the header grows or shrinks (e.g. when the nav menu wraps to 2 rows),
      // without waiting for the next scroll or resize event. measureHeader wraps
      // its work in rAF, which also avoids "ResizeObserver loop" reflow feedback.
      if (window.ResizeObserver) {
        new ResizeObserver(() => this.measureHeader()).observe(this);
      }

      // Mobile Navigation transparent header support.
      setTimeout(() => {
        this.mobile_nav = document.querySelector('.header-mobile-navigation');

        if (this.mobile_nav && this.classList.contains('transparent--true')) {
          this.mobile_nav.classList.add('is-fixed');
        }
      }, 100);
      // Buttons.
      this.menu.querySelectorAll('summary').forEach((summary) => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      this.menu.querySelectorAll('.parent-link-back--button').forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
    }
    // Write a CSS variable only when its value changed, so we don't re-trigger
    // the ResizeObserver / redundant style recalcs with an identical value.
    writeVar(name, value) {
      if (this._varCache[name] === value) return;
      this._varCache[name] = value;
      document.documentElement.style.setProperty(name, value + 'px');
    }
    // Scroll-frame work: read all geometry first, then perform all writes.
    onScrollFrame() {
      // ---- Reads ----
      const rectTop = this.getBoundingClientRect().top;
      const headerOffset = this.header_section.getBoundingClientRect().top;
      const scrollTop = document.documentElement.scrollTop;
      const scrollY = window.scrollY;
      const headerHeight = this.clientHeight;
      const announcementHeight = this._announcementBar ? this._announcementBar.clientHeight : null;

      // ---- Writes ----
      if (this.classList.contains('header-sticky--active')) {
        const offset = parseInt(rectTop, 10) + scrollTop;
        this.classList.toggle('is-sticky', scrollY >= offset && scrollY > 0);
      }
      this.writeVar('--header-offset', headerOffset);
      this.writeVar('--header-height', headerHeight);
      if (announcementHeight !== null) this.writeVar('--announcement-height', announcementHeight);

      this.handleScroll(scrollTop);
    }
    // Full header measurement (fonts.ready / load / resize / ResizeObserver).
    // rAF-batched: all reads first, then all writes.
    measureHeader() {
      window.requestAnimationFrame(() => {
        // ---- Reads ----
        const headerHeight = this.clientHeight;
        const headerOffset = this.header_section.getBoundingClientRect().top;
        const searchform = this.querySelector('.searchform');
        const navHeight = searchform ? searchform.offsetTop : headerHeight;
        const announcementHeight = this._announcementBar ? this._announcementBar.clientHeight : null;

        // ---- Writes ----
        this.writeVar('--header-height', headerHeight);
        this.writeVar('--header-offset', headerOffset);
        this.writeVar('--header-nav-height', navHeight);
        if (announcementHeight !== null) this.writeVar('--announcement-height', announcementHeight);
      });
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.link-container');
      const isOpen = detailsElement.hasAttribute('open');
      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }, 100);
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }

    handleScroll(scrollTop) {
      if (scrollTop === undefined) scrollTop = document.documentElement.scrollTop;
      if (!this.isTransitioning && this.classList.contains('header-hide-menu--active')) {
        if (scrollTop < 200) {
          this.showMenu();
        } else if (this.lastScrollPos < scrollTop) {
          this.hideMenu();
        } else if (this.lastScrollPos - 5 > scrollTop) {
          this.showMenu();
        }

        this.lastScrollPos = scrollTop;
      }
    }

    showMenu() {
      this.classList.remove('header--unpin');
      this.isTransitioning = true;
      setTimeout(() => {
        this.lastScrollPos = document.documentElement.scrollTop;
        this.isTransitioning = false;
        this.handleScroll();
      }, 300);
    }
    hideMenu() {
      if (!this.isTransitioning) {
        this.classList.add('header--unpin');
        this.isTransitioning = true;
        setTimeout(() => {
          this.lastScrollPos = document.documentElement.scrollTop;
          this.isTransitioning = false;
          this.handleScroll();
        }, 300);
      }
    }
  }
  customElements.define('theme-header', ThemeHeader);
}

if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();
      this.submenus = this.querySelectorAll('.thb-full-menu > .menu-item-has-children:not(.menu-item-has-megamenu) > .sub-menu');
    }

    connectedCallback() {
      const _this = this;

      // Resize on initial load
      document.fonts.ready.then(() => {
        window.addEventListener(
          'resize',
          debounce(() => {
            if (!this.submenus.length) {
              return;
            }
            _this.resizeSubMenus();
          }, 100)
        );
      });
    }

    resizeSubMenus() {
      const viewportWidth = window.innerWidth;

      // Read all geometry first, then apply class writes in a second pass so we
      // never read a rect right after mutating a sibling's classes (forced reflow).
      const measurements = [];
      this.submenus.forEach((submenu) => {
        const subSubmenus = submenu.querySelectorAll(':scope > .menu-item-has-children > .sub-menu');
        subSubmenus.forEach((subSubmenu) => {
          const submenuRect = subSubmenu.getBoundingClientRect();
          const submenuRightEdge = submenuRect.left + submenuRect.width;
          measurements.push({
            element: subSubmenu.parentElement,
            isOverflowing: submenuRightEdge > viewportWidth,
          });
        });
      });

      measurements.forEach(({ element, isOverflowing }) => {
        element.classList.toggle('left-submenu', isOverflowing);
      });
    }
  }

  customElements.define('full-menu', FullMenu);
}

if (!customElements.get('customer-service')) {
  class CustomerService extends HTMLElement {
    connectedCallback() {
      this.dropdown = this.querySelector('.ht-cs-dropdown');
      this.trigger = this.querySelector('.ht-cs-trigger');

      this.trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        this.dropdown.style.display === 'none' ? this.open() : this.close();
      });

      this.addEventListener('mouseenter', () => this.open());
      this.addEventListener('mouseleave', () => this.close());

      document.addEventListener('click', () => this.close());
    }

    open() {
      this.dropdown.style.display = 'block';
    }

    close() {
      this.dropdown.style.display = 'none';
    }
  }

  customElements.define('customer-service', CustomerService);
}
